import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import checkIsValidEmail from 'utils/checkIsValidEmail';

import { setModal } from 'modules/_shared/redux/modalActions';

import { EmailData } from 'types';
import { sendTestEmail } from 'modules/communications/api/sendTestEmail';
import { SendTestEmailModalContextType } from '../types/SendTestEmailModalContextType';

import { sendTestEmailModalInitValue } from '../constants/sendTestEmailModalInitValue';

const SendTestEmailModalContext = createContext<SendTestEmailModalContextType>(
  sendTestEmailModalInitValue
);

export const SendTestEmailModalProvider: FC<{
  children: ReactNode;
  emailData: EmailData;
}> = ({ children, emailData }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const [recipientEmail, setRecipientEmail] = useState<string>();
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeRecipientEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value;
    const validEmail: boolean = checkIsValidEmail(newEmail);

    setInvalidEmail(!validEmail);
    setRecipientEmail(newEmail);
  };

  const handleSendEmail = async () => {
    try {
      setIsLoading(true);
      const DEFAULT_RECIPIENT = 'Destinatario';

      const testEmailData = {
        ...emailData,
        to: [{ email: recipientEmail?.trim(), name: DEFAULT_RECIPIENT }],
      };

      await sendTestEmail({ emailData: testEmailData });
      successSwal(t('TestEmailSentSuccessfully'));
      setIsLoading(false);
      handleCloseModal();
    } catch (error) {
      errorSwal(t('ErrorSendingTestEmail'));
      setIsLoading(false);
    }
  };

  return (
    <SendTestEmailModalContext.Provider
      value={{
        data: {
          isLoading,
          recipientEmail,
          invalidEmail,
        },
        actions: {
          handleChangeRecipientEmail,
          handleCloseModal,
          handleSendEmail,
        },
      }}
    >
      {children}
    </SendTestEmailModalContext.Provider>
  );
};

export const useSendTestEmailModal = () =>
  useContext(SendTestEmailModalContext);
